import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    SelectChangeEvent,
    Grid,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    FormControlLabel,
    Checkbox,
    Chip,
} from '@mui/material';
import { Close, Save, Check } from '@mui/icons-material';
import { useNotify } from 'react-admin';
import { DeliveryMethodEnum } from '../../../utils/enum';
import { AIPurchasedTicket, HistorySaleRecord } from '../models/seat';
import MDateControl from '../../../components/controls/MDateControl';
import { formatDateTime, toLocalTime } from '../../../components/ex_dayjs';
import { dataProvider } from '../../../DataProvider';
import { dayjs as mydayjs } from '../../../components/ex_dayjs';
import { getActionChipColor } from './tabs/shared/SeatRecommendationComponent';
import { TrackingVersion } from '../AnalysisHistorical';

/**
 * Props for the SeatActionDialog component
 */
interface SeatActionDialogProps {
    /** Whether the dialog is open */
    open: boolean;
    /** Callback when dialog is closed */
    onClose: () => void;
    /** Callback when save action is triggered with seat data */
    onSave: (seatData: AIPurchasedTicket) => void;
    /** Initial seat data for editing (optional) */
    initialData?: AIPurchasedTicket | null;
}

/**
 * Returns the MUI theme color for the status border based on purchase status.
 * This function maps status values to MUI theme color paths (e.g., 'primary.main')
 * for use with the `sx` prop on Box components.
 *
 * @param status - The purchase status string (e.g., 'Buy', 'Sold', 'Expired')
 * @returns MUI theme color path string for use in sx borderColor prop
 */
const getStatusBorderColor = (status: string | undefined | null): string => {
    if (!status) return 'grey.500';
    let statusColor = getActionChipColor(status);
    return statusColor + '.main';
};

/**
 * Status options for seat management
 * 0: new, 1: buy, 2: active (renamed from "buy and list"), 3: ignore, 4: non-viable
 */
const PURCHASE_STATUS_OPTIONS = [
    { id: 'New', value: 'New', name: 'New' },
    { id: 'Buy', value: 'Buy', name: 'Buy' },
    { id: 'Active', value: 'Active', name: 'Active' },
    { id: 'Sold', value: 'Sold', name: 'Sold' },
    { id: 'PartiallySold', value: 'PartiallySold', name: 'Partially Sold' },
    { id: 'PartiallyExpired', value: 'PartiallyExpired', name: 'Partially Expired' },
    { id: 'Expired', value: 'Expired', name: 'Expired' },
    { id: 'Ignore', value: 'Ignore', name: 'Ignore' },
    { id: 'NonViable', value: 'NonViable', name: 'Non Viable' },
];

/**
 * SeatActionDialog Component
 *
 * Dialog for managing seat actions with input fields for seat details.
 * Displays a two-column layout separating original recommendation data (read-only)
 * from purchase details (editable). Shows additional purchase fields when status
 * contains "Buy".
 *
 * Features:
 * - Two-column layout: Original Recommendation (left) vs Purchase Details (right)
 * - Conditional purchase fields (visible when status not includes "New")
 * - DeliveryMethod field using DeliveryMethodEnum values
 * - Field validation for required purchase fields
 * - Support for creating new seat entries or editing existing ones
 *
 * @param props - Component props
 * @returns Dialog component for seat action management
 */
const SeatActionDialog: React.FC<SeatActionDialogProps> = ({
    open,
    onClose,
    onSave,
    initialData = null
}) => {
    const [seatData, setSeatData] = useState<AIPurchasedTicket | null>(null);
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);
    const notify = useNotify();

    const SELLING_PRICE_FACTOR = 1 - 0.0875 - 0.18; // 8.75% fee + 18% tax
    const CAD2USD = 0.8; // Example conversion rate, replace with actual if needed
    const [lastEditedField, setLastEditedField] = useState<keyof AIPurchasedTicket | null>('actualPricePerTicket');
    const [pageState, setPageState] = useState({ useConvertCAD: false });
    /**
     * Initialize or reset form data when dialog opens or initialData changes
     */
    useEffect(() => {
        if (open) {
            if (initialData) {
                const sellingPrice = initialData.sellingPrice ?? (
                    initialData.actualPricePerTicket
                        ? Number((initialData.actualPricePerTicket / SELLING_PRICE_FACTOR).toFixed(2))
                        : null
                );
                setSeatData({ ...initialData, sellingPrice });
            }
            
        } else {
            setSeatData(null);
            setIsHistoryLoading(false);
            setLastEditedField(null);
            setPageState({ useConvertCAD: false });
            return;
        }

        if (shouldShowSalesHistory()) {
            // setSeatData(initialData);
            const fetchSalesHistory = async () => {
                setIsHistoryLoading(true);
                dataProvider.postData('PurchasedTickets', 'GetHistorySaleForSeat', initialData)
                    .then((history) => {
                        if (history && history.data) {
                            setSeatData((prev) => ({
                                ...(prev as AIPurchasedTicket),
                                salesHistory: history.data as HistorySaleRecord[] || [],
                            }));
                        }
                    })
                    .catch((error) => {
                        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                        notify(`Error fetching sales history: ${errorMessage}`, { type: 'error' });
                    })
                    .finally(() => {
                        setIsHistoryLoading(false);
                    });
            };

            fetchSalesHistory();
        }
    }, [open, initialData]);

    /**
     * Handle interdependent field calculations
     * Updates related fields when one of the key fields changes
     */
    useEffect(() => {
        if (!seatData || !lastEditedField) return;

        // Create a copy to modify
        const updatedData = { ...seatData };
        const quantity = seatData.purchasedQuantity ?? 0;

        // Calculate other fields based on the recently edited field
        switch (lastEditedField) {
            case 'purchasedQuantity': {
                const pricePerTicket = seatData.actualPricePerTicket ?? 0;
                if (quantity > 0 && pricePerTicket > 0) {
                    updatedData.totalPurchaseCost = parseFloat((pricePerTicket * quantity).toFixed(2));
                } else {
                    updatedData.totalPurchaseCost = null;
                }
                break;
            }
            case 'actualPricePerTicket': {
                const pricePerTicket = seatData.actualPricePerTicket ?? null;
                if (pricePerTicket !== null && pricePerTicket > 0) {
                    // Calculate Total Cost
                    if (quantity > 0) {
                        updatedData.totalPurchaseCost = parseFloat((pricePerTicket * quantity).toFixed(2));
                    }
                    // Calculate Selling Price
                    updatedData.sellingPrice = parseFloat((pricePerTicket / SELLING_PRICE_FACTOR).toFixed(2));
                } else {
                    // If actual price is cleared, clear related fields as well
                    updatedData.totalPurchaseCost = null;
                    updatedData.sellingPrice = null;
                }
                break;
            }
            case 'totalPurchaseCost': {
                const totalCost = seatData.totalPurchaseCost ?? null;
                if (totalCost !== null && totalCost > 0 && quantity > 0) {
                    // Calculate Actual Price Per Ticket
                    const pricePerTicket = parseFloat((totalCost / quantity).toFixed(2));
                    updatedData.actualPricePerTicket = pricePerTicket;
                    // Calculate Selling Price
                    updatedData.sellingPrice = parseFloat((pricePerTicket / SELLING_PRICE_FACTOR).toFixed(2));
                } else {
                    updatedData.actualPricePerTicket = null;
                    updatedData.sellingPrice = null;
                }
                break;
            }
            case 'sellingPrice': {
                const sellingPrice = seatData.sellingPrice ?? null;
                if (sellingPrice !== null && sellingPrice > 0) {
                    // Calculate Actual Price Per Ticket
                    const pricePerTicket = parseFloat((sellingPrice * SELLING_PRICE_FACTOR).toFixed(2));
                    updatedData.actualPricePerTicket = pricePerTicket;
                    // Calculate Total Cost
                    if (quantity > 0) {
                        updatedData.totalPurchaseCost = parseFloat((pricePerTicket * quantity).toFixed(2));
                    }
                } else {
                    updatedData.actualPricePerTicket = null;
                    updatedData.totalPurchaseCost = null;
                }
                break;
            }
            case 'account':
            case 'orderNumber': {
                const newInternalNote = `Real Ticket Testing: ${seatData?.orderNumber} | ${seatData?.account} | ${initialData?.marketType} | SiteID: ${initialData?.siteId} | Additional Info: `.trim();
                // only update internalNote if it has changed and old lenght is less than new length, and keep existing additional info
                if (!seatData?.internalNote || (seatData?.internalNote?.includes('Additional Info:') && seatData?.internalNote !== newInternalNote)) {
                    let addtionalInfo = seatData.internalNote?.split('Additional Info:')[1] || '';
                    updatedData.internalNote = newInternalNote + addtionalInfo;
                }
                break;
            }
            default:
                // Do nothing if the edited field is not one of the 4 fields above
                return;
        }

        // Update state once with all changes
        setSeatData(updatedData);

    }, [lastEditedField, lastEditedField && seatData?.[lastEditedField]]); // Only trigger when the edited field changes


    /**     * Handle numeric input changes
     * @param field - The field name to update
     * @param value - The new value as string
     */
    const handleNumericInputChange = (
        field: keyof AIPurchasedTicket,
        value: string
    ) => {
        setLastEditedField(field); // Mark this as the "source of truth"
        setSeatData((prev) => {
            if (!prev) return null;
            let parsedValue: number | null = null;
            if (value !== '') {
                // Allow decimal input for price fields
                parsedValue = field === 'purchasedQuantity' ? parseInt(value, 10) : parseFloat(value);
                if (isNaN(parsedValue)) {
                    parsedValue = null;
                }
            }

            return {
                ...prev,
                [field]: parsedValue,
            };
        });
    };

    /**
     * Handle text input changes
     * @param field - The field name to update
     * @param value - The new value (can be string, number, or null)
     */
    const handleInputChange = (field: keyof AIPurchasedTicket, value: string | Date | number | boolean | null) => {
        const relatedINoteFields = ['account', 'orderNumber'];
        if (relatedINoteFields.includes(field) && typeof value === 'string') {
            setLastEditedField(field);
        }
        setSeatData((prev) => ({
            ...(prev as AIPurchasedTicket),
            [field]: value,
        }));
    };

    /**
     * Handle status dropdown change
     * @param event - The select change event
     */
    const handleStatusChange = (event: SelectChangeEvent<string>) => {
        setSeatData((prev) => ({
            ...(prev as AIPurchasedTicket),
            purchaseStatus: event.target.value as string,
        }));
    };

    /**
     * Validates the current seat data
     * @returns true if all required fields are filled and valid
     */
    const isFormValid = (): boolean => {
        if (!seatData) return false;
        // Check required fields: section, row, seat (validate editable purchase fields)
        const hasRequiredFields = !!((seatData.purchasedSectionName ?? '').trim() && (seatData.purchasedRowName ?? '').trim() && (seatData.purchasedSeatRange ?? '').trim());

        // Price validation: actualPricePerTicket must be > 0 and < 100,000
        const isPriceValid = seatData.actualPricePerTicket !== undefined && seatData.actualPricePerTicket !== null && seatData.actualPricePerTicket > 0 && seatData.actualPricePerTicket < 100000;

        return hasRequiredFields && isPriceValid;
    };

    /**
     * Handle save button click
     * Validates and saves the seat data
     * Note: While the save button is disabled when form is invalid, we keep this check
     * as a safeguard in case the component is used in different contexts or if there are
     * edge cases where the button state might not update correctly.
     */
    const handleSave = () => {
        if (!isFormValid()) {
            notify('Please fill in all required fields', { type: 'warning' });
            return;
        };

        onSave(seatData!); // Non-null assertion operator
        onClose();
    };

    /**
     * Handle cancel/close
     */
    const handleCancel = (event: any, reason: any) => {
        if (reason && reason === 'backdropClick') {
            return;
        }
        onClose();
    };

    /**
     * Determines if the purchase fields should be shown based on status
     * @returns true if status is not 'new' or 'nonviable'
     */
    const shouldShowPurchaseFields = (): boolean => {
        const keywords = ['new', 'nonviable'];
        const status = seatData?.purchaseStatus?.toLowerCase();
        return !!status && !keywords.some(keyword => status.includes(keyword));
    };
    /**
     * Determines if sales history section should be shown based on seatData id
     * @returns true if initialData has valid id and purchaseStatus is not 'New', 'Ignore', or 'NonViable'
     */
    const shouldShowSalesHistory = (): boolean => {
        return !!initialData && !!initialData?.id && !(['New', 'Ignore', 'NonViable'].includes(initialData.purchaseStatus));
    };
  
    return (
        <Dialog open={open} onClose={handleCancel} maxWidth="lg" fullWidth>
            <DialogTitle>
                {shouldShowSalesHistory() ? 'Update Purchased Seat Data' : (initialData?.originalAIScore ? 'Purchase Seat in Existing Analysis' : 'Add Purchase Seat Manually')}
                {/* Row 1: Event, Venue (normalized), Event Date */}
                <Box sx={{ mt: 0 }}>
                    <Typography variant="subtitle2" component="span" sx={{ color: 'info.main', fontWeight: 'bold' }}>
                        <span style={{ color: 'white' }}>Event:</span> {initialData?.eventName || '-'}
                    </Typography>
                    {/* Conditionally show Venue only if initialData.venue has a value */}
                    {initialData?.venue && (
                        <>
                            <Typography variant="caption" component="span" sx={{ mx: 1 }}>|</Typography>
                            <Typography variant="subtitle2" component="span" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                                <span style={{ color: 'white' }}>Venue:</span> {initialData.venue}
                            </Typography>
                        </>
                    )}
                    <Typography variant="caption" component="span" sx={{ mx: 1 }}>|</Typography>
                    <Typography variant="subtitle2" component="span" sx={{ color: 'secondary.main', fontWeight: 'bold' }}>
                        <span style={{ color: 'white' }}>Date:</span> {formatDateTime(initialData?.eventDate?.replace(/z$/i, ''), 'MM/DD/YYYY hh:mm A') || '-'}
                    </Typography>
                </Box>
                {/* Row 2: Site ID, Purchased By, Purchase Date */}
                <Box sx={{ mt: 0 }}>
                    <Typography variant="subtitle2" component="span" sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                        <span style={{ color: 'white' }}>Site ID:</span> {initialData?.siteId || '-'}
                    </Typography>
                    {initialData?.purchasedBy_UserId && (
                        <>
                            <Typography variant="caption" component="span" sx={{ mx: 1 }}>|</Typography>
                            <Typography variant="subtitle2" component="span" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                                <span style={{ color: 'white' }}>Purchased By:</span> {initialData.purchasedBy_UserId}
                            </Typography>
                        </>
                    )}
                    {initialData?.purchaseDate && (
                        <>
                            <Typography variant="caption" component="span" sx={{ mx: 1 }}>|</Typography>
                            <Typography variant="subtitle2" component="span" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                                <span style={{ color: 'white' }}>Purchase Date:</span> {formatDateTime(toLocalTime(initialData.purchaseDate), 'MM/DD/YYYY hh:mm A')}
                            </Typography>
                        </>
                    )}
                </Box>
            </DialogTitle>
            <DialogContent>
                <Grid container size={12}>
                    <Grid size={12} display="flex" justifyContent="center" alignItems="center">
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                Original Values
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                            <TextField
                                label="Section"
                                value={seatData?.originalSectionName || ''}
                                sx={{ width: '50%' }}
                                size="small"
                                disabled
                                    
                            />
                            <TextField
                                label="Row"
                                value={seatData?.originalRowName || ''}
                                sx={{ width: '15%' }}
                                size="small"
                                disabled
                                   
                            />
                            <TextField
                                label="Seat Range"
                                value={seatData?.originalSeatRange || ''}
                                sx={{ width: '22%' }}
                                size="small"
                                disabled
                                  
                            />
                            <TextField
                                label="Quantity"
                                type="number"
                                value={seatData?.originalQuantity || 0}
                                sx={{ width: '25%' }}
                                size="small"
                                disabled
                                   
                            />
                            <TextField
                                label="Price Per Ticket"
                                type="number"
                                value={seatData?.originalPricePerTicket || 0}
                                sx={{ width: '40%' }}
                                size="small"
                                disabled
                            />
                        </Box>
                    </Grid>

                    <Grid size={12} display="flex" justifyContent="center" alignItems="center">
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                Purchase Details
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                            <TextField
                                label="Section"
                                value={seatData?.purchasedSectionName || ''}
                                onChange={(e) => handleInputChange('purchasedSectionName', e.target.value)}
                                sx={{ width: '50%' }}
                                required
                                size="small"
                            />
                            <TextField
                                label="Row"
                                value={seatData?.purchasedRowName || ''}
                                onChange={(e) => handleInputChange('purchasedRowName', e.target.value)}
                                sx={{ width: '15%' }}
                                required
                                size="small"
                            />
                            <TextField
                                label="Seat Range"
                                value={seatData?.purchasedSeatRange || ''}
                                onChange={(e) => handleInputChange('purchasedSeatRange', e.target.value)}
                                sx={{ width: '22%' }}
                                required
                                size="small"
                            />
                            <TextField
                                label="Quantity"
                                type="number"
                                value={seatData?.purchasedQuantity || ''}
                                onChange={(e) => handleNumericInputChange('purchasedQuantity', e.target.value)}
                                sx={{ width: '25%' }}
                                required
                                size="small"
                            />
                            <TextField
                                label="Actual Price Per Ticket"
                                type="number"
                                value={seatData?.actualPricePerTicket || ''}
                                required
                                onChange={(e) => {
                                    // When actual price changes, reset the CAD conversion flag
                                    setPageState((prev) => ({ ...prev, useConvertCAD: false }));
                                    handleNumericInputChange('actualPricePerTicket', e.target.value);
                                }}
                                sx={{ width: '40%' }}
                                size="small"
                                helperText={
                                    seatData?.actualPricePerTicket !== undefined && seatData?.actualPricePerTicket !== null && (seatData?.actualPricePerTicket <= 0 || seatData?.actualPricePerTicket >= 100000)
                                        ? 'Price must be between $0 and $100,000'
                                        : ''
                                }
                                error={seatData?.actualPricePerTicket !== undefined && seatData?.actualPricePerTicket !== null && (seatData?.actualPricePerTicket <= 0 || seatData?.actualPricePerTicket >= 100000)}
                            />
                            <TextField
                                label="Total Cost"
                                type="number"
                                value={seatData?.totalPurchaseCost || ''}
                                onChange={(e) => handleNumericInputChange('totalPurchaseCost', e.target.value)}
                                sx={{ width: '25%' }}
                                size="small"
                            />
                            <Chip label="CAD->USD" size="small" disabled={pageState.useConvertCAD} color='primary' //sx={{ fontSize: '13px' }}
                                onClick={() => {
                                    // Convert CAD to USD for actualPricePerTicket
                                    if (seatData?.actualPricePerTicket) {
                                        const convertedPriceStr = (seatData.actualPricePerTicket * CAD2USD).toFixed(2);
                                        setPageState((prev) => ({ ...prev, useConvertCAD: true }));
                                        handleNumericInputChange('actualPricePerTicket', convertedPriceStr);
                                        notify('Converted [Actual Price Per Ticket] from CAD to USD', { type: 'info' });
                                    }
                                }}
                                sx={{
                                    fontSize: '10px',
                                    height: '6%',
                                    borderRadius: '4px',        // rounded corners
                                    writingMode: 'vertical-rl', // Rotate text vertically
                                    textOrientation: 'mixed',   // Text orientation
                                    padding: '2px 0',
                                    minWidth: '24px',           // Minimum width
                                    '& .MuiChip-label': {
                                        padding: '0 4px',       // Reduce text padding
                                        whiteSpace: 'nowrap'
                                    }
                                }}
                            />
                        </Box>
                    </Grid>
                </Grid>
                <Grid container columnSpacing={2} size={12}>
                    <Grid size={6}>
                        {/* Box wrapper for status border highlight based on purchaseStatus */}
                        <Box
                            sx={{
                                borderBottom: 2,
                                borderBottomColor: getStatusBorderColor(seatData?.purchaseStatus),
                                boxShadow: `0px 5px 10px -2px ${getStatusBorderColor(seatData?.purchaseStatus)}`,
                                borderRadius: 2,
                                p: 0.5,
                                paddingBottom: 0
                            }}
                        >
                            <FormControl fullWidth size="small" required>
                                <InputLabel id="status-select-label">Status</InputLabel>
                                <Select
                                    labelId="status-select-label"
                                    id="status-select"
                                    value={seatData?.purchaseStatus || ''}
                                    onChange={handleStatusChange}
                                    label="Status"
                                >
                                    {PURCHASE_STATUS_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Grid>
                    <Grid size={6}>
                        <MDateControl
                            label="Purchase Date"
                            defaultValue={formatDateTime(toLocalTime(seatData?.purchaseDate) || null)}
                            value={seatData?.purchaseDate || null}
                            onChange={(newValue: any) => {
                                // update hour,min,sec to current time's hour,min,sec
                                const currentTime = new Date();
                                const updatedValue = new Date(newValue);
                                updatedValue.setHours(currentTime.getHours());
                                updatedValue.setMinutes(currentTime.getMinutes());
                                updatedValue.setSeconds(currentTime.getSeconds());
                                handleInputChange('purchaseDate', updatedValue);
                            }}
                        />
                    </Grid>
                </Grid>
                {/* Conditional Purchase Fields - Only shown when status contains "Buy" */}
                {shouldShowPurchaseFields() && (
                    <Grid container columnSpacing={2} size={12}>
                        <Grid size={3}>
                            <TextField
                                label="Primary ID"
                                value={seatData?.orderNumber ?? ''}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    handleInputChange('orderNumber', val || null);
                                }}
                                size="small"
                            />
                        </Grid>
                        <Grid size={3}>
                            <FormControl  size="small">
                                <InputLabel id="delivery-method-label">Delivery Method</InputLabel>
                                <Select
                                    labelId="delivery-method-label"
                                    value={seatData?.deliveryMethod?.toString() || '0'}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        const parsed = parseInt(val, 10);
                                        handleInputChange('deliveryMethod', isNaN(parsed) ? 0 : parsed);
                                    }}
                                    label="Delivery Method"
                                >
                                    {DeliveryMethodEnum.map((method) => (
                                        <MenuItem key={method.id} value={method.id}>
                                            {method.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={6}>
                            <TextField
                                label="Selling Price"
                                type="number"
                                value={seatData?.sellingPrice || ''}
                                onChange={(e) => handleNumericInputChange('sellingPrice', e.target.value)}
                                size="small"
                                placeholder="Price per Ticket/ (1 - 0.0875 - 0.18)"
                                sx={{
                                    '& .MuiInputBase-input::placeholder': {
                                        fontSize: '12px',
                                        color: 'gray',
                                        opacity: 1,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={6}>
                            <TextField
                                label="Ticket Link"
                                value={seatData?.ticketLink || ''}
                                onChange={(e) => handleInputChange('ticketLink', e.target.value)}
                                size="small"
                                placeholder="https://..."
                                fullWidth
                            />
                        </Grid>
                        <Grid size={6}>
                            <TextField
                                label="Account"
                                value={seatData?.account || ''}
                                onChange={(e) => handleInputChange('account', e.target.value)}
                                size="small"
                                placeholder="Account name or identifier"
                                fullWidth
                            />
                        </Grid>
                        <Grid size={6}>
                            <TextField
                                label="Public Note"
                                slotProps={{
                                    input: {
                                        sx: { '& textarea': { resize: 'vertical' } }
                                    }
                                }}
                                value={seatData?.publicNote || ''}
                                onChange={(e) => handleInputChange('publicNote', e.target.value)}
                                size="small"
                                multiline
                                rows={2}
                                placeholder="Customer-facing notes"
                            />
                        </Grid>
                        <Grid size={6}>
                            <TextField
                                label="Internal Note"
                                slotProps={{
                                    input: {
                                        sx: { '& textarea': { resize: 'vertical' } }
                                    }
                                }}
                                value={seatData?.internalNote || ''}
                                onChange={(e) => handleInputChange('internalNote', e.target.value)}
                                size="small"
                                multiline
                                rows={2}
                                placeholder="Internal team notes"
                            />
                        </Grid>
                        <Grid size={6}>
                            <TextField
                                label="Supervisor Notes"
                                slotProps={{
                                    input: {
                                        sx: { '& textarea': { resize: 'vertical' } }
                                    }
                                }}
                                value={seatData?.supervisorNotes || ''}
                                onChange={(e) => handleInputChange('supervisorNotes', e.target.value)}
                                size="small"
                                multiline
                                rows={2}
                            />
                        </Grid>
                        <Grid container size={6} spacing={2} alignItems='flex-end'>
                            <Grid size={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="tracking-version-label">Tracking Version</InputLabel>
                                    <Select
                                        labelId="tracking-version-label"
                                        value={seatData?.trackingVersion || ''}
                                        label="Tracking Version"
                                        onChange={(e: SelectChangeEvent) => handleInputChange('trackingVersion', e.target.value || null)}
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        {TrackingVersion?.map((version: any) => (
                                            <MenuItem key={version.id} value={version.id}>
                                                {version.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={4}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={seatData?.highRisk === true}
                                            onChange={(e) => handleInputChange('highRisk', e.target.checked)}
                                        />
                                    }
                                    label="High Risk" sx={{ marginTop: '15px' }}
                                />
                            </Grid>
                           
                        </Grid>
                    </Grid>
                )}
                {/* Sales History Section - Only shown if seatData has a valid id */}
                {shouldShowSalesHistory() && (
                    <Grid item xs={12} sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Sales History
                            {/* Display sales history summary with order count and profit total */}
                            {seatData?.salesHistory && seatData.salesHistory.length > 0 && (() => {
                                const orderCount = seatData.salesHistory.length;
                                // Calculate profit sum: sum(unitTicketSales - cost) for each record
                                const profitSum = seatData.salesHistory.reduce((sum, sale) => {
                                    const profit = (sale.unitTicketSales ?? 0) - (sale.cost ?? 0);
                                    return sum + profit * (sale.quantity ?? 1);
                                }, 0);
                                const profitColor = profitSum > 0 ? 'success.main' : profitSum < 0 ? 'error.main' : 'text.primary';
                                return (
                                    <Typography component="span" sx={{ ml: 1, fontWeight: 'normal', color: profitColor }}>
                                    [{orderCount} orders, total profit: ${profitSum.toFixed(2)}]
                                    </Typography>
                                );
                            })()}
                        </Typography>

                        {/* Sales history table or loading indicator */}
                        {isHistoryLoading ? (
                            <Typography variant="body2">Loading history...</Typography>
                        ) : (
                            <TableContainer component={Paper}>
                                <Table size="small" aria-label="sales history table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Order</TableCell>
                                            <TableCell>Order Date</TableCell>
                                            <TableCell>Listed Date</TableCell>
                                            <TableCell>Updated Date</TableCell>
                                            <TableCell>Section</TableCell>
                                            <TableCell>Seats/Row</TableCell>
                                            <TableCell align="right">Quantity</TableCell>
                                            <TableCell align="right">Cost</TableCell>
                                            <TableCell align="right">List Price</TableCell>
                                            <TableCell align="right">Profit</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {/* check if salesHistory exist and length > 0 */}
                                        {seatData?.salesHistory && seatData.salesHistory.length > 0 ? (
                                            seatData.salesHistory.map((sale, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>{sale.order_number || '-'}</TableCell>
                                                    <TableCell>{sale.order_date ? <>{formatDateTime(sale.order_date.replace(/z/i, ''), 'MM/DD/YYYY')}
                                                        <br/>
                                                        {formatDateTime(sale.order_date.replace(/z/i, ''), 'hh:mm A')}</> : '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {(() => {
                                                            const listedDate = sale.listed_date;
                                                            const orderDate = sale.order_date;
                                                            
                                                            // Handle null or undefined dates
                                                            if (!listedDate) {
                                                                return '-';
                                                            }
                                                            
                                                            try {
                                                                // Format the listed_date date in UTC
                                                                const formattedDate = formatDateTime(toLocalTime(listedDate), 'MM/DD/YYYY - hh:mm A');
                                                                let diffElement;
                                                                // Calculate days difference if both dates are available
                                                                if (orderDate) {
                                                                    const listedDateParsed = mydayjs.utc(listedDate);
                                                                    const orderDateParsed = mydayjs.utc(orderDate);

                                                                    // Calculate the difference in days
                                                                    // Positive value means orderDate is after listedDate (item was listed then sold)
                                                                    const daysDiff = orderDateParsed.diff(listedDateParsed, 'day');
                                                                    diffElement = (
                                                                        <span style={{ color: 'gray' }}>({daysDiff} days difference)</span>
                                                                    );
                                                                }
                                                                return (
                                                                    <div style={{
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        justifyContent: 'center', // vertical center
                                                                        height: '100%',
                                                                        lineHeight: 1.4,
                                                                    }}>
                                                                        {formattedDate}
                                                                        <br />
                                                                        {diffElement}
                                                                    </div>
                                                                );
                                                            } catch {
                                                                // Return fallback if date parsing fails
                                                                return '-';
                                                            }
                                                        })()}
                                                    </TableCell>
                                                    <TableCell>{sale.updated_on ? <>{formatDateTime(sale.updated_on.replace(/z/i, ''), 'MM/DD/YYYY')}
                                                        <br/>
                                                        {formatDateTime(sale.updated_on.replace(/z/i, ''), 'hh:mm A')}</> : '-'}
                                                    </TableCell>
                                                    <TableCell>{sale.section || '-'}</TableCell>
                                                    <TableCell>{<>{sale.seats} / <span style={{ color: 'orange' }}>{`[${sale.row}]`}</span></> || '-'}</TableCell>
                                                    <TableCell align="right">{sale.quantity || '-'}</TableCell>
                                                    <TableCell align="right">
                                                        {sale.cost !== null && sale.cost !== undefined ? `$${sale.cost.toFixed(2)}` : '-'}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {sale.listPrice !== null && sale.listPrice !== undefined ? `$${sale.listPrice.toFixed(2)}` : '-'}
                                                    </TableCell>
                                                    <TableCell align="right"
                                                        sx={{
                                                            color: !!sale.unitTicketSales && (sale.unitTicketSales - (sale.cost ?? 0)) > 0
                                                                ? 'success.main'
                                                                : !!sale.unitTicketSales && (sale.unitTicketSales - (sale.cost ?? 0)) < 0
                                                                    ? 'error.main'
                                                                    : 'text.primary',
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        {!!sale.unitTicketSales ? `$${(sale.unitTicketSales - (sale.cost ?? 0)).toFixed(2)}` : '-'}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center">
                                                No sales history found.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Grid>
                )}
                
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel} startIcon={<Close />}>
                    Cancel
                </Button>
                {/* Relist button - only shown when PurchaseStatus is "Buy" */}
                {seatData?.purchaseStatus === 'Buy' && (
                    <Button
                        onClick={() => {
                            // Update status to Active and save
                            const updatedData = {
                                ...seatData,
                                purchaseStatus: 'Active',
                            };
                            onSave(updatedData);
                            onClose();
                        }}
                        color="success"
                        variant='contained'
                        startIcon={<Check />}
                    >
                        Relist
                    </Button>
                )}
                <Button
                    onClick={handleSave}
                    startIcon={<Save />}
                    color="primary"
                    variant="contained"
                    disabled={!isFormValid()}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SeatActionDialog;
export { PURCHASE_STATUS_OPTIONS };
